import { useState, useEffect, useMemo } from 'react';
import { client } from '@/api/client';
import { toast } from 'sonner';
import { X, UserCheck, Search, Link2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { computeLoadGross } from '@/lib/invoiceHelpers';
import { nextTripNumber, attachLoadToTrip, listLoadsForTrip, computeTripStops } from '@/lib/trips';

const TRIP_TYPES = ['Company', 'Broker', 'Empty Move'];
const STATUSES = ['Planned', 'Dispatched', 'In Transit', 'Completed'];
const EQUIPMENT_TYPES = ['Van', 'Reefer', 'Flatbed', 'Step Deck', 'Other'];

const Field = ({ label, children }) => (
  <div>
    <label className="text-xs text-muted-foreground block mb-1">{label}</label>
    {children}
  </div>
);

// mode: 'trip' (Company/Broker, default) | 'empty-move' (no loads, just a
// deadhead/repositioning move -- driver + truck + miles, no revenue).
export default function TripFormModal({ trip, mode = 'trip', drivers, trucks, trailers, onClose, onSaved }) {
  const [savedTrip, setSavedTrip] = useState(trip || null);
  const [form, setForm] = useState({
    trip_number: trip?.trip_number || '',
    trip_type: trip?.trip_type || (mode === 'empty-move' ? 'Empty Move' : 'Company'),
    status: trip?.status || 'Planned',
    assigned_driver_email: trip?.assigned_driver_email || '',
    assigned_driver_name: trip?.assigned_driver_name || '',
    truck_id: trip?.truck_id || '',
    trailer_id: trip?.trailer_id || '',
    origin: trip?.origin || '',
    destination: trip?.destination || '',
    origin_name: trip?.origin_name || '',
    destination_name: trip?.destination_name || '',
    pickup_date: trip?.pickup_date || '',
    delivery_date: trip?.delivery_date || '',
    total_miles: trip?.total_miles ?? '',
    equipment_type: trip?.equipment_type || 'Van',
    temp_min: trip?.temp_min ?? '',
    temp_max: trip?.temp_max ?? '',
    temp_actual: trip?.temp_actual ?? '',
  });
  const [saving, setSaving] = useState(false);
  const [attachedLoads, setAttachedLoads] = useState([]); // [{link, load}]
  const [loadSearch, setLoadSearch] = useState('');
  const [allLoads, setAllLoads] = useState([]);
  const [loadTripCounts, setLoadTripCounts] = useState(new Map()); // load_id -> # trips already on

  const isEmptyMove = form.trip_type === 'Empty Move';
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  useEffect(() => {
    if (!trip) {
      client.entities.Trip.list('-created_date', 300).then(existing => {
        set('trip_number', nextTripNumber(existing));
      }).catch(() => {});
    } else {
      refreshAttached(trip.id);
    }
    client.entities.Load.list('-created_date', 500).then(setAllLoads).catch(() => {});
    client.entities.TripLoad.list('-created_date', 1000).then(links => {
      const counts = new Map();
      for (const l of links) counts.set(l.load_id, (counts.get(l.load_id) || 0) + 1);
      setLoadTripCounts(counts);
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refreshAttached = async (tripId) => {
    const links = await listLoadsForTrip(tripId);
    setAttachedLoads(links);
    // Attaching/detaching a load re-derives the trip's own origin/
    // destination/pickup_date/delivery_date server-side (see
    // syncTripSummaryFromLoads) -- pull the fresh values back into the
    // form so the (disabled, load-derived) date fields don't show stale
    // data from before this attach/detach.
    try {
      const fresh = await client.entities.Trip.get(tripId);
      if (fresh) {
        setForm(f => ({
          ...f,
          origin: fresh.origin || f.origin,
          destination: fresh.destination || f.destination,
          origin_name: fresh.origin_name ?? f.origin_name,
          destination_name: fresh.destination_name ?? f.destination_name,
          pickup_date: fresh.pickup_date || f.pickup_date,
          delivery_date: fresh.delivery_date || f.delivery_date,
        }));
      }
    } catch { /* non-critical -- list view will show the real values regardless */ }
  };

  const handleDriverSelect = (email) => {
    const d = drivers.find(x => x.user_email === email);
    set('assigned_driver_email', email);
    set('assigned_driver_name', d?.full_name || '');
  };

  const handleSave = async () => {
    if (!form.trip_number.trim()) { toast.error('Trip # is required.'); return; }
    if (isEmptyMove && (!form.origin.trim() || !form.destination.trim())) {
      toast.error('Empty moves need an origin and destination -- there are no loads to derive them from.');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        trip_number: form.trip_number.trim(),
        trip_type: form.trip_type,
        status: form.status,
        assigned_driver_email: form.assigned_driver_email,
        assigned_driver_name: form.assigned_driver_name,
        truck_id: form.truck_id || null,
        trailer_id: form.trailer_id || null,
        origin: form.origin,
        destination: form.destination,
        origin_name: form.origin_name,
        destination_name: form.destination_name,
        pickup_date: form.pickup_date || null,
        delivery_date: form.delivery_date || null,
        total_miles: form.total_miles === '' ? null : Number(form.total_miles),
        equipment_type: form.equipment_type,
        temp_min: form.temp_min === '' ? null : Number(form.temp_min),
        temp_max: form.temp_max === '' ? null : Number(form.temp_max),
        temp_actual: form.temp_actual === '' ? null : Number(form.temp_actual),
      };
      if (savedTrip) {
        await client.entities.Trip.update(savedTrip.id, payload);
        toast.success('Trip updated.');
        if (isEmptyMove) { onSaved(); return; }
        setSavedTrip({ ...savedTrip, ...payload });
      } else {
        const created = await client.entities.Trip.create(payload);
        toast.success(isEmptyMove ? 'Empty move logged.' : 'Trip created — attach loads below.');
        if (isEmptyMove) { onSaved(); return; }
        setSavedTrip(created);
      }
    } catch (e) {
      toast.error('Failed to save trip.');
    } finally {
      setSaving(false);
    }
  };

  const attachedLoadIds = useMemo(() => new Set(attachedLoads.map(a => a.load.id)), [attachedLoads]);

  const searchResults = useMemo(() => {
    if (!loadSearch.trim()) return [];
    const q = loadSearch.trim().toLowerCase();
    return allLoads
      .filter(l => !attachedLoadIds.has(l.id))
      .filter(l => [l.load_number, l.customer_name, l.origin, l.destination].filter(Boolean).some(v => String(v).toLowerCase().includes(q)))
      .slice(0, 15);
  }, [loadSearch, allLoads, attachedLoadIds]);

  const handleAttach = async (load) => {
    if (!savedTrip) return;
    try {
      await attachLoadToTrip(savedTrip.id, load.id);
      toast.success(`Load #${load.load_number} attached.`);
      setLoadSearch('');
      refreshAttached(savedTrip.id);
    } catch (e) {
      toast.error('Failed to attach load.');
    }
  };

  const attachedRevenue = attachedLoads.reduce((sum, a) => sum + computeLoadGross(a.load), 0);
  const tripStops = computeTripStops(attachedLoads.map(a => a.load));

  return (
    <>
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40" onClick={onClose} />
      <div className="fixed inset-0 z-50 overflow-y-auto">
        <div className="flex min-h-full items-start justify-center p-4">
          <div className="relative w-full max-w-2xl my-8 rounded-2xl border border-border p-6 space-y-5 bg-background">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="font-space font-semibold text-foreground">{savedTrip ? `Edit Trip ${savedTrip.trip_number}` : (isEmptyMove ? 'Log Empty Move' : 'New Trip')}</h2>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {isEmptyMove ? 'Deadhead / repositioning move -- no revenue, driver + truck + miles only.' : 'Assign a driver + truck to one or more loads.'}
                </p>
              </div>
              <button onClick={onClose} className="text-muted-foreground hover:text-foreground transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <Field label="Trip #">
                <Input value={form.trip_number} onChange={e => set('trip_number', e.target.value)} className="text-foreground" />
              </Field>
              <Field label="Type">
                <select
                  value={form.trip_type}
                  onChange={e => set('trip_type', e.target.value)}
                  className="w-full px-3 py-2 rounded-lg bg-transparent border border-input text-foreground text-sm"
                >
                  {TRIP_TYPES.map(t => <option key={t} value={t} className="bg-background">{t}</option>)}
                </select>
              </Field>
              <Field label="Status">
                <select
                  value={form.status}
                  onChange={e => set('status', e.target.value)}
                  className="w-full px-3 py-2 rounded-lg bg-transparent border border-input text-foreground text-sm"
                >
                  {STATUSES.map(s => <option key={s} value={s} className="bg-background">{s}</option>)}
                </select>
              </Field>
            </div>

            <div>
              <label className="text-xs text-muted-foreground block mb-1">Driver</label>
              <div className="relative">
                <UserCheck className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <select
                  value={form.assigned_driver_email}
                  onChange={e => handleDriverSelect(e.target.value)}
                  className="w-full pl-9 pr-3 py-2 rounded-lg bg-transparent border border-input text-foreground text-sm"
                >
                  <option value="" className="bg-background text-muted-foreground">Unassigned</option>
                  {drivers.map(d => (
                    <option key={d.id} value={d.user_email} className="bg-background">{d.full_name}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Truck">
                <select
                  value={form.truck_id}
                  onChange={e => set('truck_id', e.target.value)}
                  className="w-full px-3 py-2 rounded-lg bg-transparent border border-input text-foreground text-sm"
                >
                  <option value="" className="bg-background text-muted-foreground">— None —</option>
                  {trucks.map(t => <option key={t.id} value={t.id} className="bg-background">Unit {t.unit_number}</option>)}
                </select>
              </Field>
              <Field label="Trailer">
                <select
                  value={form.trailer_id}
                  onChange={e => set('trailer_id', e.target.value)}
                  className="w-full px-3 py-2 rounded-lg bg-transparent border border-input text-foreground text-sm"
                >
                  <option value="" className="bg-background text-muted-foreground">— None —</option>
                  {trailers.map(t => <option key={t.id} value={t.id} className="bg-background">Unit {t.unit_number}</option>)}
                </select>
              </Field>
            </div>

            <Field label="Equipment">
              <select
                value={form.equipment_type}
                onChange={e => set('equipment_type', e.target.value)}
                className="w-full px-3 py-2 rounded-lg bg-transparent border border-input text-foreground text-sm"
              >
                {EQUIPMENT_TYPES.map(t => <option key={t} value={t} className="bg-background">{t}</option>)}
              </select>
            </Field>

            {isEmptyMove ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label="From"><Input value={form.origin} onChange={e => set('origin', e.target.value)} placeholder="City, ST" className="text-foreground" /></Field>
                <Field label="To"><Input value={form.destination} onChange={e => set('destination', e.target.value)} placeholder="City, ST" className="text-foreground" /></Field>
              </div>
            ) : (
              <>
                <p className="text-xs text-muted-foreground bg-muted/30 border border-border rounded-lg px-3 py-2">
                  From / To and Pickup / Delivery are derived automatically from whatever loads are attached below (earliest pickup → latest delivery).
                </p>
                {(form.origin_name || form.destination_name) && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                    <div><span className="text-xs text-muted-foreground block mb-1">Origin Facility</span><span className="text-foreground">{form.origin_name || '—'}</span></div>
                    <div><span className="text-xs text-muted-foreground block mb-1">Destination Facility</span><span className="text-foreground">{form.destination_name || '—'}</span></div>
                  </div>
                )}
              </>
            )}

            {form.equipment_type === 'Reefer' && (
              <div className="grid grid-cols-3 gap-4">
                <Field label="Temp Min (°F)"><Input type="number" value={form.temp_min} onChange={e => set('temp_min', e.target.value)} className="text-foreground" /></Field>
                <Field label="Temp Max (°F)"><Input type="number" value={form.temp_max} onChange={e => set('temp_max', e.target.value)} className="text-foreground" /></Field>
                <Field label="Actual (°F)"><Input type="number" value={form.temp_actual} onChange={e => set('temp_actual', e.target.value)} className="text-foreground" /></Field>
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Pickup Date">
                <Input type="date" value={form.pickup_date || ''} onChange={e => set('pickup_date', e.target.value)} className="text-foreground" disabled={!isEmptyMove && attachedLoads.length > 0} />
              </Field>
              <Field label="Delivery Date">
                <Input type="date" value={form.delivery_date || ''} onChange={e => set('delivery_date', e.target.value)} className="text-foreground" disabled={!isEmptyMove && attachedLoads.length > 0} />
              </Field>
            </div>

            <Field label="Total Miles">
              <Input type="number" value={form.total_miles} onChange={e => set('total_miles', e.target.value)} placeholder="Odometer / PC*Miler estimate" className="text-foreground" />
            </Field>

            {!isEmptyMove && savedTrip && (
              <div className="space-y-3 pt-2 border-t border-border">
                <div className="flex items-center justify-between">
                  <h3 className="text-xs font-semibold text-primary uppercase tracking-widest">Loads on this Trip ({attachedLoads.length}) · {tripStops} stop{tripStops !== 1 ? 's' : ''}</h3>
                  {attachedRevenue > 0 && <span className="text-xs text-muted-foreground">Total: ${attachedRevenue.toLocaleString('en-US', { maximumFractionDigits: 0 })}</span>}
                </div>
                {attachedLoads.length > 0 && (
                  <div className="space-y-1.5">
                    {attachedLoads.map(a => (
                      <div key={a.link.id} className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white/5 text-sm">
                        <Link2 className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
                        <span className="text-foreground font-medium">#{a.load.load_number}</span>
                        <span className="text-muted-foreground truncate">{a.load.origin} → {a.load.destination}</span>
                        {a.link.leg_sequence > 1 && <span className="text-xs text-amber-400">leg {a.link.leg_sequence}</span>}
                      </div>
                    ))}
                  </div>
                )}
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    value={loadSearch}
                    onChange={e => setLoadSearch(e.target.value)}
                    placeholder="Search load # or customer to attach…"
                    className="pl-9 text-foreground"
                  />
                </div>
                {searchResults.length > 0 && (
                  <div className="space-y-1 max-h-48 overflow-y-auto rounded-lg border border-border">
                    {searchResults.map(l => {
                      const onOtherTrip = loadTripCounts.get(l.id) > 0;
                      return (
                        <button
                          key={l.id}
                          onClick={() => handleAttach(l)}
                          className="w-full flex items-center justify-between px-3 py-2 text-sm hover:bg-white/5 transition-colors text-left"
                        >
                          <span className="text-foreground">#{l.load_number} — {l.customer_name || 'No customer'}</span>
                          <span className="text-xs text-muted-foreground">{l.origin} → {l.destination}{onOtherTrip ? ' · already on another trip (multi-leg)' : ''}</span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            <div className="flex gap-3 pt-2">
              <Button onClick={handleSave} disabled={saving} className="flex-1 bg-primary text-primary-foreground">
                {saving ? 'Saving…' : savedTrip ? 'Save Changes' : (isEmptyMove ? 'Log Empty Move' : 'Create Trip')}
              </Button>
              <Button onClick={onClose} variant="outline">{savedTrip && !isEmptyMove ? 'Done' : 'Cancel'}</Button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
